/**
 * Switch — accessible toggle component wired to the .nv-toggle glass token.
 *
 * Renders as a styled <button role=switch> using the .nv-toggle CSS class
 * for track shape and colour (background, border-radius, transition). The
 * animated knob is an explicit child <span> — the .nv-toggle::after
 * pseudo-element is suppressed via after:hidden to avoid a duplicate knob.
 * The .is-on modifier is added when checked to apply the active gradient.
 */
import * as React from 'react';
import { cn } from '../../lib/cn';

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
  'aria-label'?: string;
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  function Switch(
    { checked, onCheckedChange, disabled = false, className, ...props },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={props['aria-label']}
        data-testid={props['data-testid']}
        disabled={disabled}
        onClick={() => {
          onCheckedChange(!checked);
        }}
        className={cn(
          // .nv-toggle provides: track shape, size (22x38px), base background, transition
          // after:hidden suppresses the ::after pseudo-element knob since we use an explicit span
          'nv-toggle after:hidden shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          checked && 'is-on',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none inline-block h-[18px] w-[18px] rounded-full glass shadow-sm transition-transform',
            // Translate matches .nv-toggle::after: left:2px base, translateX(16px) when on
            checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
          )}
        />
      </button>
    );
  },
);
